'use client';

import type { Property } from '@/lib/api/clients/properties';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import {
  type UnifiedListingItem,
  type ItemTypeFilter,
  transformPropertyToUnified,
  transformProjectToUnified,
  isPropertyItem,
  isProjectItem,
  filterByItemType,
} from '@/components/listings/unified-types';
import Image from 'next/image';
import Link from 'next/link';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { Bed, Bath, Maximize, SlidersHorizontal, X, Building2, Calendar, Home } from 'lucide-react';
import {
  StoneSearchBar,
  StoneSortControl,
  StoneFilterPanel,
  StoneViewSwitcher,
  StonePagination,
  HorizonOverlay,
  type FilterSection,
  type ViewMode,
  type SortOption,
} from '@routeroyal/theme-stone-investment';
import { detectRegion } from '@routeroyal/theme-stone-investment';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPriceSimple } from '@/lib/utils/price';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

type Props = {
  properties: Property[];
  projects?: PropertyDevelopmentProject[]; // Optional - for mixed listing views
  locale: 'en' | 'fr' | 'es';
  messages: Record<string, unknown>;
  basePath: string;
};

type Region = 'ALL' | 'PROVENCE' | 'MAURITIUS';
type SortValue = 'newest' | 'price-asc' | 'price-desc' | 'bedrooms-desc' | 'name-asc';

// Filter state interface
interface FilterState {
  search: string;
  region: Region;
  itemType: ItemTypeFilter; // 'all' | 'properties' | 'projects'
  propertyTypes: string[];
  minBedrooms: number | null;
  maxBedrooms: number | null;
  minBathrooms: number | null;
  maxBathrooms: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  sortBy: SortValue;
  viewMode: ViewMode;
  page: number;
}

const ITEMS_PER_PAGE = 8;

const defaultFilters: FilterState = {
  search: '',
  region: 'ALL',
  itemType: 'all',
  propertyTypes: [],
  minBedrooms: null,
  maxBedrooms: null,
  minBathrooms: null,
  maxBathrooms: null,
  minPrice: null,
  maxPrice: null,
  sortBy: 'newest',
  viewMode: 'grid',
  page: 1,
};

/**
 * Stone Investment Properties Content
 * 
 * Full-featured property listing with:
 * - Search bar with debounce
 * - Sort control (newest, price, bedrooms)
 * - Filter panel (region, property type, bedrooms, bathrooms, price)
 * - Grid/List view toggle
 * - Pagination
 * - URL parameter sync for shareable filters
 */
export function PropertiesContentStone({ 
  properties, 
  projects = [],
  locale, 
  messages,
  basePath 
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const urlSyncRef = useRef<NodeJS.Timeout>(undefined);
  const isOwnUrlUpdate = useRef(false);
  
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  
  // Check if we have projects to show (mixed view mode)
  const hasMixedListings = projects.length > 0;
  
  // Filter out properties that belong to any development — these are shown on
  // the development detail page instead of the property listing.
  const standaloneProperties = useMemo(() => {
    return properties.filter((p) => !p.developmentId);
  }, [properties]);

  // Transform data to unified items for mixed view
  const unifiedItems = useMemo(() => {
    const unifiedProperties = standaloneProperties.map(transformPropertyToUnified);
    const unifiedProjects = projects.map(transformProjectToUnified);
    return [...unifiedProperties, ...unifiedProjects];
  }, [standaloneProperties, projects]);
  
  // Initialize filters from URL params
  const [filters, setFilters] = useState<FilterState>(() => {
    const params = searchParams;
    return {
      search: params.get('q') || '',
      region: (params.get('region')?.toUpperCase() as Region) || 'ALL',
      itemType: (params.get('type') as ItemTypeFilter) || 'all',
      propertyTypes: params.get('types')?.split(',').filter(Boolean) || [],
      minBedrooms: params.get('minBeds') ? parseInt(params.get('minBeds')!) : null,
      maxBedrooms: params.get('maxBeds') ? parseInt(params.get('maxBeds')!) : null,
      minBathrooms: params.get('minBaths') ? parseInt(params.get('minBaths')!) : null,
      maxBathrooms: params.get('maxBaths') ? parseInt(params.get('maxBaths')!) : null,
      minPrice: params.get('minPrice') ? parseInt(params.get('minPrice')!) : null,
      maxPrice: params.get('maxPrice') ? parseInt(params.get('maxPrice')!) : null,
      sortBy: (params.get('sort') as SortValue) || 'newest',
      viewMode: (params.get('view') as ViewMode) || 'grid',
      page: params.get('page') ? parseInt(params.get('page')!) : 1,
    };
  });

  // Get translation messages
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const propertiesMessages = (stoneMessages.properties || {}) as Record<string, string>;
  const filtersMessages = (stoneMessages.filters || {}) as Record<string, string>;
  const developmentMessages = (stoneMessages.development || {}) as Record<string, unknown>;
  const devTypeMessages = (developmentMessages.type || {}) as Record<string, string>;
  const devStatusMessages = (developmentMessages.status || {}) as Record<string, string>;
  const listingMessages = (stoneMessages.listing || {}) as Record<string, string>;
  const navMessages = (stoneMessages.propertyNavigation || {}) as Record<string, string>;
  
  const t = useCallback((key: string, fallback: string): string => {
    return propertiesMessages[key] || filtersMessages[key] || fallback;
  }, [propertiesMessages, filtersMessages]);

  // Sync URL search params into filter state only on external navigation
  // (e.g. browser back/forward), not on our own URL writes
  useEffect(() => {
    if (isOwnUrlUpdate.current) {
      isOwnUrlUpdate.current = false;
      return;
    }

    const urlRegion = (searchParams.get('region')?.toUpperCase() as Region) || 'ALL';
    const urlSearch = searchParams.get('q') || '';
    const urlItemType = (searchParams.get('type') as ItemTypeFilter) || 'all';
    const urlSort = (searchParams.get('sort') as SortValue) || 'newest';
    const urlView = (searchParams.get('view') as ViewMode) || 'grid';
    const urlPage = searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1;
    const urlTypes = searchParams.get('types')?.split(',').filter(Boolean) || [];
    const urlMinBeds = searchParams.get('minBeds') ? parseInt(searchParams.get('minBeds')!) : null;
    const urlMaxBeds = searchParams.get('maxBeds') ? parseInt(searchParams.get('maxBeds')!) : null;
    const urlMinBaths = searchParams.get('minBaths') ? parseInt(searchParams.get('minBaths')!) : null;
    const urlMaxBaths = searchParams.get('maxBaths') ? parseInt(searchParams.get('maxBaths')!) : null;
    const urlMinPrice = searchParams.get('minPrice') ? parseInt(searchParams.get('minPrice')!) : null;
    const urlMaxPrice = searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')!) : null;

    setFilters(prev => {
      const next: FilterState = {
        search: urlSearch,
        region: urlRegion,
        itemType: urlItemType,
        propertyTypes: urlTypes,
        minBedrooms: urlMinBeds,
        maxBedrooms: urlMaxBeds,
        minBathrooms: urlMinBaths,
        maxBathrooms: urlMaxBaths,
        minPrice: urlMinPrice,
        maxPrice: urlMaxPrice,
        sortBy: urlSort,
        viewMode: urlView,
        page: urlPage,
      };
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [searchParams]);

  // Debounced sync of filters to URL — prevents rapid URL writes while typing
  useEffect(() => {
    if (urlSyncRef.current) clearTimeout(urlSyncRef.current);

    urlSyncRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      
      if (filters.search) params.set('q', filters.search);
      if (filters.region !== 'ALL') params.set('region', filters.region);
      if (filters.itemType !== 'all') params.set('type', filters.itemType);
      if (filters.propertyTypes.length > 0) params.set('types', filters.propertyTypes.join(','));
      if (filters.minBedrooms !== null) params.set('minBeds', filters.minBedrooms.toString());
      if (filters.maxBedrooms !== null) params.set('maxBeds', filters.maxBedrooms.toString());
      if (filters.minBathrooms !== null) params.set('minBaths', filters.minBathrooms.toString());
      if (filters.maxBathrooms !== null) params.set('maxBaths', filters.maxBathrooms.toString());
      if (filters.minPrice !== null) params.set('minPrice', filters.minPrice.toString());
      if (filters.maxPrice !== null) params.set('maxPrice', filters.maxPrice.toString());
      if (filters.sortBy !== 'newest') params.set('sort', filters.sortBy);
      if (filters.viewMode !== 'grid') params.set('view', filters.viewMode);
      if (filters.page > 1) params.set('page', filters.page.toString());
      
      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
      
      isOwnUrlUpdate.current = true;
      router.replace(newUrl, { scroll: false });
    }, 500);

    return () => {
      if (urlSyncRef.current) clearTimeout(urlSyncRef.current);
    };
  }, [filters, pathname, router]);

  // Extract unique property types from standalone properties only
  const propertyTypes = useMemo(() => {
    const types = new Map<string, { name: string; count: number }>();
    standaloneProperties.forEach(p => {
      if (p.propertyType) {
        const existing = types.get(p.propertyType.id);
        if (existing) {
          existing.count++;
        } else {
          types.set(p.propertyType.id, { name: p.propertyType.name, count: 1 });
        }
      }
    });
    return Array.from(types.entries()).map(([id, { name, count }]) => ({
      value: id,
      label: name,
      count,
    }));
  }, [standaloneProperties]);

  // Determine region from unified item
  const getItemRegion = useCallback((item: UnifiedListingItem): Region => {
    const countryCode = item.location?.countryCode?.toLowerCase() || '';

    // Prefer structured countryCode for reliable classification
    if (countryCode === 'fr') return 'PROVENCE';
    if (countryCode === 'mu') return 'MAURITIUS';

    // Fall back to keyword matching on name + location text
    let combinedText = item.name?.toLowerCase() || '';
    if (item.location) {
      combinedText += ` ${item.location.region?.toLowerCase() || ''} ${item.location.country?.toLowerCase() || ''}`;
    }
    if (isProjectItem(item)) {
      combinedText += ` ${item._originalProject.name?.toLowerCase() || ''}`;
    }

    if (combinedText.includes('provence') || combinedText.includes('gordes') ||
        combinedText.includes('luberon') || combinedText.includes('france')) {
      return 'PROVENCE';
    }
    if (combinedText.includes('mauritius') || combinedText.includes('maurice')) {
      return 'MAURITIUS';
    }
    return 'MAURITIUS';
  }, []);

  const getItemPrice = useCallback((item: UnifiedListingItem): number | null => {
    if (isPropertyItem(item)) {
      return extractBestPriceSimple(item._originalProperty).amount;
    }
    return null;
  }, []);

  // Legacy helpers for backward compatibility with PropertyCard
  const getPropertyRegion = useCallback((property: Property): Region => {
    const name = property.name?.toLowerCase() || '';
    const locationName = (property.location?.region || property.location?.administrativeAreaName || '')?.toLowerCase();
    const country = (property.location?.country || property.location?.countryName || '')?.toLowerCase();
    const countryCode = property.location?.countryCode?.toLowerCase() || '';
    
    const combinedText = `${name} ${locationName} ${country} ${countryCode}`;
    
    if (combinedText.includes('provence') || combinedText.includes('gordes') || 
        combinedText.includes('luberon') || combinedText.includes('france') || countryCode === 'fr') {
      return 'PROVENCE';
    }
    if (combinedText.includes('mauritius') || countryCode === 'mu') {
      return 'MAURITIUS';
    }
    return 'ALL';
  }, []);

  const getPropertyPrice = useCallback((property: Property): number | null => {
    return extractBestPriceSimple(property).amount;
  }, []);

  // Filter and sort unified items (works for both properties-only and mixed views)
  const filteredItems = useMemo(() => {
    let result = [...unifiedItems];
    
    // Item type filter (only applies in mixed view)
    if (hasMixedListings && filters.itemType !== 'all') {
      result = filterByItemType(result, filters.itemType);
    }
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(item => {
        const searchableText = [
          item.name,
          item.description,
          item.location?.name,
          item.location?.city,
          item.location?.district,
          item.location?.region,
          item.location?.suburb,
          item.location?.neighbourhood,
          item.location?.country,
          isPropertyItem(item) ? item.propertyType?.name : item.projectType,
        ].filter(Boolean).join(' ').toLowerCase();
        return searchableText.includes(searchLower);
      });
    }
    
    // Region filter
    if (filters.region !== 'ALL') {
      result = result.filter(item => getItemRegion(item) === filters.region);
    }
    
    // Property type filter (only applies to properties)
    if (filters.propertyTypes.length > 0) {
      result = result.filter(item => {
        if (isProjectItem(item)) return true; // Don't filter out projects
        return item.propertyType && filters.propertyTypes.includes(item.propertyType.id);
      });
    }
    
    // Bedrooms filter (only applies to properties)
    if (filters.minBedrooms !== null) {
      result = result.filter(item => {
        if (isProjectItem(item)) return true; // Don't filter out projects
        return (item.bedrooms || 0) >= filters.minBedrooms!;
      });
    }
    if (filters.maxBedrooms !== null) {
      result = result.filter(item => {
        if (isProjectItem(item)) return true; // Don't filter out projects
        return (item.bedrooms || 0) <= filters.maxBedrooms!;
      });
    }
    
    // Bathrooms filter (only applies to properties)
    if (filters.minBathrooms !== null) {
      result = result.filter(item => {
        if (isProjectItem(item)) return true; // Don't filter out projects
        return (item.bathrooms || 0) >= filters.minBathrooms!;
      });
    }
    if (filters.maxBathrooms !== null) {
      result = result.filter(item => {
        if (isProjectItem(item)) return true; // Don't filter out projects
        return (item.bathrooms || 0) <= filters.maxBathrooms!;
      });
    }
    
    // Price filter (only applies to properties with numeric prices)
    if (filters.minPrice !== null) {
      result = result.filter(item => {
        const price = getItemPrice(item);
        if (price === null) return true; // Don't filter out items without price
        return price >= filters.minPrice!;
      });
    }
    if (filters.maxPrice !== null) {
      result = result.filter(item => {
        const price = getItemPrice(item);
        if (price === null) return true; // Don't filter out items without price
        return price <= filters.maxPrice!;
      });
    }
    
    // Sort
    result.sort((a, b) => {
      switch (filters.sortBy) {
        case 'price-asc': {
          const priceA = getItemPrice(a) || 0;
          const priceB = getItemPrice(b) || 0;
          return priceA - priceB;
        }
        case 'price-desc': {
          const priceA = getItemPrice(a) || 0;
          const priceB = getItemPrice(b) || 0;
          return priceB - priceA;
        }
        case 'bedrooms-desc': {
          const bedsA = isPropertyItem(a) ? (a.bedrooms || 0) : 0;
          const bedsB = isPropertyItem(b) ? (b.bedrooms || 0) : 0;
          return bedsB - bedsA;
        }
        case 'name-asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'newest':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });
    
    return result;
  }, [unifiedItems, filters, hasMixedListings, getItemRegion, getItemPrice]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const start = (filters.page - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, filters.page]);
  
  // Count items by type for display
  const itemCounts = useMemo(() => {
    const propertyCount = filteredItems.filter(isPropertyItem).length;
    const projectCount = filteredItems.filter(isProjectItem).length;
    return { properties: propertyCount, projects: projectCount, total: filteredItems.length };
  }, [filteredItems]);

  // Fire listing_view event when paginated results change
  useEffect(() => {
    if (paginatedItems.length > 0) {
      analyticsEvents.propertyListView(
        paginatedItems.map(p => ({ id: p.id, name: p.name })),
        'search_results'
      );
    }
  }, [paginatedItems]);

  // Reset page when filters change
  const updateFilters = useCallback((updates: Partial<FilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...updates,
      // Reset to page 1 when filters change (except page/view changes)
      page: 'page' in updates || 'viewMode' in updates ? (updates.page || prev.page) : 1,
    }));
  }, []);

  // Sort options
  const sortOptions: SortOption[] = [
    { value: 'newest', label: t('sortNewest', 'Newest First') },
    { value: 'price-asc', label: t('sortPriceAsc', 'Price: Low to High') },
    { value: 'price-desc', label: t('sortPriceDesc', 'Price: High to Low') },
    { value: 'bedrooms-desc', label: t('sortBedrooms', 'Most Bedrooms') },
    { value: 'name-asc', label: t('sortName', 'Name A-Z') },
  ];

  // Filter panel sections
  const filterSections: FilterSection[] = [
    {
      id: 'region',
      label: t('filterRegion', 'Region'),
      type: 'radio',
      defaultExpanded: true,
      options: [
        { value: 'ALL', label: t('allRegions', 'All Regions'), count: standaloneProperties.length },
        { value: 'PROVENCE', label: t('provence', 'Provence'), count: standaloneProperties.filter(p => getPropertyRegion(p) === 'PROVENCE').length },
        { value: 'MAURITIUS', label: t('mauritius', 'Mauritius'), count: standaloneProperties.filter(p => getPropertyRegion(p) === 'MAURITIUS').length },
      ],
    },
    {
      id: 'propertyTypes',
      label: t('filterPropertyType', 'Property Type'),
      type: 'checkbox',
      defaultExpanded: true,
      options: propertyTypes,
    },
    {
      id: 'bedrooms',
      label: t('filterBedrooms', 'Bedrooms'),
      type: 'range',
      min: 0,
      max: 10,
      step: 1,
      defaultExpanded: false,
    },
    {
      id: 'bathrooms',
      label: t('filterBathrooms', 'Bathrooms'),
      type: 'range',
      min: 0,
      max: 10,
      step: 1,
      defaultExpanded: false,
    },
    {
      id: 'price',
      label: t('filterPrice', 'Price'),
      type: 'price-range',
      min: 0,
      max: 50000000,
      step: 100000,
      defaultExpanded: false,
    },
  ];

  // Convert filters to panel values format
  const filterPanelValues = useMemo(() => {
    const values: Record<string, string | string[] | { min?: number; max?: number }> = {};
    
    if (filters.region !== 'ALL') {
      values.region = filters.region;
    }
    if (filters.propertyTypes.length > 0) {
      values.propertyTypes = filters.propertyTypes;
    }
    if (filters.minBedrooms !== null || filters.maxBedrooms !== null) {
      const bedroomRange: { min?: number; max?: number } = {};
      if (filters.minBedrooms !== null) bedroomRange.min = filters.minBedrooms;
      if (filters.maxBedrooms !== null) bedroomRange.max = filters.maxBedrooms;
      values.bedrooms = bedroomRange;
    }
    if (filters.minBathrooms !== null || filters.maxBathrooms !== null) {
      const bathroomRange: { min?: number; max?: number } = {};
      if (filters.minBathrooms !== null) bathroomRange.min = filters.minBathrooms;
      if (filters.maxBathrooms !== null) bathroomRange.max = filters.maxBathrooms;
      values.bathrooms = bathroomRange;
    }
    if (filters.minPrice !== null || filters.maxPrice !== null) {
      const priceRange: { min?: number; max?: number } = {};
      if (filters.minPrice !== null) priceRange.min = filters.minPrice;
      if (filters.maxPrice !== null) priceRange.max = filters.maxPrice;
      values.price = priceRange;
    }
    
    return values;
  }, [filters]);

  // Handle filter panel changes
  const handleFilterPanelChange = useCallback((values: Record<string, string | string[] | number | { min?: number; max?: number }>) => {
    const updates: Partial<FilterState> = {};
    
    // Region
    if (typeof values.region === 'string') {
      updates.region = values.region as Region;
    } else if (!values.region) {
      updates.region = 'ALL';
    }
    
    // Property types
    if (Array.isArray(values.propertyTypes)) {
      updates.propertyTypes = values.propertyTypes as string[];
    } else if (!values.propertyTypes) {
      updates.propertyTypes = [];
    }
    
    // Bedrooms
    if (typeof values.bedrooms === 'object' && values.bedrooms !== null) {
      const bedroomRange = values.bedrooms as { min?: number; max?: number };
      updates.minBedrooms = bedroomRange.min !== undefined ? bedroomRange.min : null;
      updates.maxBedrooms = bedroomRange.max !== undefined ? bedroomRange.max : null;
    } else if (!values.bedrooms) {
      updates.minBedrooms = null;
      updates.maxBedrooms = null;
    }
    
    // Bathrooms
    if (typeof values.bathrooms === 'object' && values.bathrooms !== null) {
      const bathroomRange = values.bathrooms as { min?: number; max?: number };
      updates.minBathrooms = bathroomRange.min !== undefined ? bathroomRange.min : null;
      updates.maxBathrooms = bathroomRange.max !== undefined ? bathroomRange.max : null;
    } else if (!values.bathrooms) {
      updates.minBathrooms = null;
      updates.maxBathrooms = null;
    }
    
    // Price
    if (typeof values.price === 'object' && values.price !== null) {
      const priceRange = values.price as { min?: number; max?: number };
      updates.minPrice = priceRange.min !== undefined ? priceRange.min : null;
      updates.maxPrice = priceRange.max !== undefined ? priceRange.max : null;
    } else if (!values.price) {
      updates.minPrice = null;
      updates.maxPrice = null;
    }
    
    updateFilters(updates);
  }, [updateFilters]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.region !== 'ALL') count++;
    if (filters.itemType !== 'all') count++;
    if (filters.propertyTypes.length > 0) count++;
    if (filters.minBedrooms !== null || filters.maxBedrooms !== null) count++;
    if (filters.minBathrooms !== null || filters.maxBathrooms !== null) count++;
    if (filters.minPrice !== null || filters.maxPrice !== null) count++;
    return count;
  }, [filters]);

  return (
    <>
      {/* Collection Header */}
      <section className="pt-32 pb-8 px-6 md:px-12 bg-stone-50">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-8 bg-stone-500" />
            <p className="font-sans text-sm uppercase tracking-[0.4em] text-stone-700">
              {t('eyebrow', 'Curated Properties')}
            </p>
          </div>
          <h1 className="font-serif text-6xl md:text-9xl text-stone-900 leading-[0.9] -ml-1">
            {t('title', 'The Collection')}
          </h1>
          <p className="mt-8 font-sans font-light text-stone-700 text-lg md:text-xl max-w-2xl leading-relaxed">
            {t('description', 'A carefully curated portfolio of exceptional properties in Provence and Mauritius, each selected for its character, location, and timeless beauty.')}
          </p>
        </div>
      </section>

      {/* Search & Controls Bar - z-30 to stay below menu overlay */}
      <div className="sticky top-20 z-30 bg-stone-50/95 backdrop-blur-sm border-y border-stone-200">
        <div className="container mx-auto px-6 md:px-12">
          {/* Primary Row */}
          <div className="flex items-center gap-0 h-14">
            {/* Search Bar */}
            <div className="w-full lg:w-48 shrink-0">
              <StoneSearchBar
                value={filters.search}
                onChange={(value) => updateFilters({ search: value })}
                placeholder={t('searchPlaceholder', 'Search...')}
                size="sm"
              />
            </div>

            {/* Item Type Tabs (only shown in mixed view) */}
            {hasMixedListings && (
              <div className="hidden md:flex items-center self-stretch ml-6 pl-6 border-l border-stone-200">
                {(['all', 'properties', 'projects'] as ItemTypeFilter[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => updateFilters({ itemType: type })}
                    className={`relative h-full px-3 text-xs uppercase tracking-[0.15em] font-sans transition-colors ${
                      filters.itemType === type
                        ? 'text-stone-900 font-medium'
                        : 'text-stone-500 hover:text-stone-800'
                    }`}
                  >
                    {type === 'all' ? t('allTypes', 'All') : type === 'properties' ? t('properties', 'Properties') : t('projects', 'Projects')}
                    {filters.itemType === type && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-stone-900" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Region Tabs */}
            <div className="hidden md:flex items-center self-stretch ml-6 pl-6 border-l border-stone-200">
              {(['ALL', 'PROVENCE', 'MAURITIUS'] as Region[]).map((region) => (
                <button
                  key={region}
                  onClick={() => updateFilters({ region })}
                  className={`relative h-full px-3 text-xs uppercase tracking-[0.15em] font-sans transition-colors ${
                    filters.region === region
                      ? 'text-stone-900 font-medium'
                      : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  {region === 'ALL' ? t('allRegions', 'All Regions') : t(region.toLowerCase(), region)}
                  {filters.region === region && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-stone-900" />
                  )}
                </button>
              ))}
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Controls Group */}
            <div className="flex items-center">
              {/* Refine Button */}
              <button
                onClick={() => setShowFiltersModal(true)}
                className="flex items-center gap-2 h-10 px-4 text-stone-600 hover:text-stone-900 transition-colors"
              >
                <SlidersHorizontal size={15} strokeWidth={1.5} />
                <span className="text-xs font-sans uppercase tracking-[0.15em]">{t('refine', 'Refine')}</span>
                {activeFilterCount > 0 && (
                  <span className="w-4.5 h-4.5 bg-stone-900 text-white text-[10px] flex items-center justify-center leading-none">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              <span className="h-5 w-px bg-stone-200 mx-1" />

              {/* Sort Control */}
              <StoneSortControl
                options={sortOptions}
                value={filters.sortBy}
                onChange={(value) => updateFilters({ sortBy: value as SortValue })}
                label={t('sortBy', 'Sort by')}
              />

              <span className="h-5 w-px bg-stone-200 mx-1" />

              {/* View Switcher */}
              <StoneViewSwitcher
                value={filters.viewMode}
                onChange={(mode) => updateFilters({ viewMode: mode })}
                labels={{
                  gridView: t('gridView', 'Grid view'),
                  listView: t('listView', 'List view'),
                }}
              />
            </div>
          </div>

          {/* Secondary Row - Filter pills and results count */}
          <div className="flex items-center gap-3 border-t border-stone-100 py-2.5">
            {/* Active Filter Pills */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {filters.search && (
                  <FilterPill
                    label={`"${filters.search}"`}
                    onRemove={() => updateFilters({ search: '' })}
                  />
                )}
                {filters.itemType !== 'all' && (
                  <FilterPill
                    label={filters.itemType === 'properties' ? t('properties', 'Properties') : t('projects', 'Projects')}
                    onRemove={() => updateFilters({ itemType: 'all' })}
                  />
                )}
                {filters.region !== 'ALL' && (
                  <FilterPill
                    label={filters.region}
                    onRemove={() => updateFilters({ region: 'ALL' })}
                  />
                )}
                {filters.propertyTypes.map(typeId => {
                  const type = propertyTypes.find(t => t.value === typeId);
                  return type ? (
                    <FilterPill
                      key={typeId}
                      label={type.label}
                      onRemove={() => updateFilters({
                        propertyTypes: filters.propertyTypes.filter(id => id !== typeId)
                      })}
                    />
                  ) : null;
                })}
                {(filters.minBedrooms !== null || filters.maxBedrooms !== null) && (
                  <FilterPill
                    label={`${filters.minBedrooms ?? 0}-${filters.maxBedrooms ?? '10+'} ${t('beds', 'beds')}`}
                    onRemove={() => updateFilters({ minBedrooms: null, maxBedrooms: null })}
                  />
                )}
                {(filters.minBathrooms !== null || filters.maxBathrooms !== null) && (
                  <FilterPill
                    label={`${filters.minBathrooms ?? 0}-${filters.maxBathrooms ?? '10+'} ${t('baths', 'baths')}`}
                    onRemove={() => updateFilters({ minBathrooms: null, maxBathrooms: null })}
                  />
                )}
                {(filters.minPrice !== null || filters.maxPrice !== null) && (
                  <FilterPill
                    label={`€${(filters.minPrice ?? 0).toLocaleString()} - €${(filters.maxPrice ?? 50000000).toLocaleString()}`}
                    onRemove={() => updateFilters({ minPrice: null, maxPrice: null })}
                  />
                )}
                <button
                  onClick={clearFilters}
                  className="text-[10px] uppercase tracking-[0.15em] text-stone-400 hover:text-stone-700 font-sans transition-colors"
                >
                  {t('clearAll', 'Clear all')}
                </button>
              </div>
            )}

            {/* Results Count - always visible, pushed right */}
            <span className="ml-auto text-xs text-stone-500 font-sans whitespace-nowrap">
              {hasMixedListings ? (
                <>
                  {itemCounts.total} {itemCounts.total === 1 ? t('result', 'result') : t('results', 'results')}
                  {filters.itemType === 'all' && itemCounts.projects > 0 && (
                    <span className="text-stone-400 ml-1">
                      ({itemCounts.properties} {t('properties', 'properties')}, {itemCounts.projects} {t('projects', 'projects')})
                    </span>
                  )}
                </>
              ) : (
                <>
                  {filteredItems.length} {filteredItems.length === 1 ? t('property', 'property') : t('properties', 'properties')}
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="bg-stone-50 py-12">
        <div className="container mx-auto px-6 md:px-12">
          {/* Property/Project Grid/List - Full Width */}
          {paginatedItems.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-serif text-2xl text-stone-900 mb-4">
                {t('noResults', 'No results found')}
              </p>
              <p className="text-stone-600 mb-6">
                {t('tryAdjusting', 'Try adjusting your filters or search terms')}
              </p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-stone-900 text-white text-sm uppercase tracking-widest font-sans hover:bg-stone-800 transition-colors"
              >
                {t('clearFilters', 'Clear Filters')}
              </button>
            </div>
          ) : filters.viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {paginatedItems.map((item) => (
                isPropertyItem(item) ? (
                  <PropertyCard
                    key={item.id}
                    property={item._originalProperty}
                    basePath={basePath}
                    locale={locale}
                    getPropertyRegion={getPropertyRegion}
                    getPropertyPrice={getPropertyPrice}
                    viewLabel={listingMessages?.view || 'View'}
                  />
                ) : (
                  <ProjectCard
                    key={item.id}
                    project={item}
                    basePath={basePath}
                    locale={locale}
                    getItemRegion={getItemRegion}
                    devTypeMessages={devTypeMessages}
                    devStatusMessages={devStatusMessages}
                    exploreLabel={navMessages?.explore || 'Explore'}
                  />
                )
              ))}
            </div>
          ) : (
            <div className="space-y-0">
              {paginatedItems.map((item) => (
                isPropertyItem(item) ? (
                  <PropertyListCard
                    key={item.id}
                    property={item._originalProperty}
                    basePath={basePath}
                    locale={locale}
                    getPropertyRegion={getPropertyRegion}
                    getPropertyPrice={getPropertyPrice}
                  />
                ) : (
                  <ProjectListCard
                    key={item.id}
                    project={item}
                    basePath={basePath}
                    locale={locale}
                    getItemRegion={getItemRegion}
                    devTypeMessages={devTypeMessages}
                    devStatusMessages={devStatusMessages}
                  />
                )
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12">
              <StonePagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => updateFilters({ page })}
                labels={{
                  previous: t('previous', 'Previous'),
                  next: t('next', 'Next'),
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Refine Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setShowFiltersModal(false)}
          />
          
          {/* Modal Panel */}
          <div className="relative w-full max-w-lg max-h-[90vh] bg-stone-50 overflow-hidden flex flex-col mx-4 md:mx-0">
            {/* Header */}
            <div className="shrink-0 bg-stone-50 px-6 py-4 border-b border-stone-200 flex items-center justify-between">
              <h2 className="font-serif text-xl text-stone-900">{t('refineSearch', 'Refine Search')}</h2>
              <button 
                onClick={() => setShowFiltersModal(false)}
                className="p-2 hover:bg-stone-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Filter Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <StoneFilterPanel
                sections={filterSections}
                values={filterPanelValues}
                onChange={handleFilterPanelChange}
                labels={{
                  title: t('refine', 'Refine'),
                  clearAll: t('clearAll', 'Clear all'),
                  property: t('property', 'property'),
                  properties: t('properties', 'properties'),
                  min: t('min', 'Min'),
                  max: t('max', 'Max'),
                }}
              />
            </div>
            
            {/* Footer */}
            <div className="shrink-0 bg-stone-50 px-6 py-4 border-t border-stone-200 flex gap-4">
              <button
                onClick={clearFilters}
                className="flex-1 py-3 border border-stone-300 text-stone-700 text-sm uppercase tracking-widest font-sans hover:bg-stone-100 transition-colors"
              >
                {t('clearAll', 'Clear all')}
              </button>
              <button
                onClick={() => setShowFiltersModal(false)}
                className="flex-1 py-3 bg-stone-900 text-white text-sm uppercase tracking-widest font-sans hover:bg-stone-800 transition-colors"
              >
                {t('showing', 'Show')} {filteredItems.length} {filteredItems.length === 1 ? t('result', 'Result') : t('results', 'Results')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Teaser */}
      <section className="relative h-[50vh] overflow-hidden">
        <Image
          src="https://res.cloudinary.com/elementary-media/image/upload/v1772728560/Generated_Image_March_05_2026_-_8_35PM_ks3dyb.png"
          alt="Contact Background"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h2 className="font-serif text-4xl md:text-6xl text-white italic mb-6">
            {t('footerTitle', 'Selling Distinction')}
          </h2>
          <p className="font-sans font-light text-stone-300 mb-8 max-w-xl">
            {t('footerSubtitle', 'Let us tell the story of your property')}
          </p>
          <Link
            href={`${basePath}/contact`}
            className="px-8 py-4 bg-white text-stone-900 text-xs uppercase tracking-widest font-sans hover:bg-stone-100 transition-colors"
          >
            {t('contactAgency', 'Contact Our Agency')}
          </Link>
        </div>
      </section>
    </>
  );
}

// Filter Pill Component
function FilterPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-stone-300 text-stone-600 text-[11px] font-sans tracking-wide">
      {label}
      <button onClick={onRemove} className="text-stone-400 hover:text-stone-800 transition-colors">
        <X size={11} strokeWidth={1.5} />
      </button>
    </span>
  );
}

// Property Card Component (Grid View) - 16:9 with HorizonOverlay flag colors
function PropertyCard({ 
  property, 
  basePath, 
  locale: _locale,
  getPropertyRegion: _getPropertyRegion,
  getPropertyPrice,
  viewLabel = 'View',
}: { 
  property: Property; 
  basePath: string;
  locale: string;
  getPropertyRegion: (p: Property) => Region;
  getPropertyPrice: (p: Property) => number | null;
  viewLabel?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const askingPrice = getPropertyPrice(property);
  
  // Detect region for flag colors using structured location data first
  const locationCountry = property.location?.country || property.location?.countryName || '';
  const locationCountryCode = property.location?.countryCode?.toLowerCase() || '';
  const isFrance = locationCountryCode === 'fr'
    || locationCountry.toLowerCase() === 'france'
    || (!locationCountry && detectRegion(
        [property.location?.region, property.location?.administrativeAreaName, property.name]
          .filter(Boolean).join(' ')
      ) === 'provence');
  
  // Derive country display label from actual location data
  const countryLabel = locationCountry
    || (locationCountryCode === 'fr' ? 'France' : locationCountryCode === 'mu' ? 'Mauritius' : '')
    || (isFrance ? 'France' : '');

  // Build location string for display
  const displayLocation = [
    property.location?.suburb,
    property.location?.city || property.location?.localityName,
    property.location?.district || property.location?.administrativeAreaName,
    property.location?.neighbourhood,
    property.location?.region,
  ].filter(Boolean).join(', ') || property.location?.name || 'Location';

  return (
    <Link 
      href={`${basePath}/properties/${property.id}`}
      className="block"
    >
      <div
        className="group relative w-full cursor-pointer flex flex-col"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image Container - 4:3 aspect ratio (consistent with project cards) */}
        <div className="relative aspect-4/3 overflow-hidden bg-stone-200">
          {property.primaryImageUrl ? (
            <Image
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              className={`object-cover transition-transform duration-2000 ease-out will-change-transform ${
                isHovered ? 'scale-105' : 'scale-100'
              }`}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200 flex items-center justify-center">
              <Home size={48} className="text-stone-400" strokeWidth={1} />
            </div>
          )}
          
          <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />
          
          {/* Center Floating "View" Button */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
            <div 
              className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                isHovered ? 'w-24 h-24 opacity-100' : 'w-12 h-12 opacity-0 translate-y-4'
              }`}
            >
              <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
              <span className="text-white font-serif italic text-xl tracking-widest drop-shadow-md">
                {viewLabel}
              </span>
            </div>
          </div>
          
          {/* Location Label - Bottom Left */}
          <div className="absolute bottom-6 left-6 z-20 overflow-hidden">
            <p 
              className={`text-[10px] uppercase tracking-[0.3em] text-stone-200 font-sans font-medium transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              {displayLocation}
            </p>
          </div>
        </div>
        
        {/* Content Section */}
        <div className="flex flex-col gap-4 pt-6">
          {/* Title */}
          <h3 className={`font-serif text-2xl md:text-3xl text-stone-900 leading-tight transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}>
            {property.name}
          </h3>

          {/* Price */}
          {askingPrice && (
            <p className="font-serif text-xl text-stone-900">
              <FormatPrice amount={askingPrice} currency={property.currency || 'EUR'} size="sm" />
            </p>
          )}

          <div className="border-t border-stone-200" />
          
          {/* Property Details */}
          <div className="flex flex-wrap items-center gap-3 text-stone-600 font-sans text-sm tracking-wider">
            {property.propertyType && (
              <>
                <span className="text-stone-900 font-medium">{property.propertyType.name}</span>
                <span className="w-px h-3 bg-stone-300" />
              </>
            )}
            {property.bedrooms && (
              <span className="flex items-center gap-1.5">
                <Bed size={14} className="text-stone-400" strokeWidth={1.5} /> {property.bedrooms}
              </span>
            )}
            {property.bathrooms && (
              <span className="flex items-center gap-1.5">
                <Bath size={14} className="text-stone-400" strokeWidth={1.5} /> {property.bathrooms}
              </span>
            )}
            {property.totalFloorArea && (
              <span className="flex items-center gap-1.5">
                <Maximize size={14} className="text-stone-400" strokeWidth={1.5} /> {property.totalFloorArea} {property.totalFloorAreaUnit?.toLowerCase() || 'm²'}
              </span>
            )}
            {countryLabel && (
              <span className={`ml-auto transition-colors duration-500 uppercase text-xs tracking-[0.2em] ${
                isFrance ? 'group-hover:text-blue-900' : 'group-hover:text-teal-800'
              }`}>
                {countryLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// Property List Card Component (List View)
function PropertyListCard({ 
  property, 
  basePath, 
  locale: _locale,
  getPropertyRegion,
  getPropertyPrice,
}: { 
  property: Property; 
  basePath: string;
  locale: string;
  getPropertyRegion: (p: Property) => Region;
  getPropertyPrice: (p: Property) => number | null;
}) {
  const region = getPropertyRegion(property);
  const askingPrice = getPropertyPrice(property);

  return (
    <Link 
      href={`${basePath}/properties/${property.id}`}
      className="group block py-6 border-b border-stone-200 hover:bg-stone-100/50 transition-colors"
    >
      <div className="flex gap-6">
        {/* Image */}
        <div className="relative w-48 h-32 shrink-0 overflow-hidden bg-stone-200">
          {property.primaryImageUrl && (
            <Image
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              className="object-cover transition-transform duration-1500 ease-out group-hover:scale-105"
            />
          )}
          <div className={`absolute top-2 left-2 h-4 w-0.5 ${
            region === 'PROVENCE' ? 'bg-amber-500' : 'bg-red-500'
          }`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className="text-xs text-stone-500 uppercase tracking-widest font-sans">
                {region}
              </span>
              <h3 className="font-serif text-xl text-stone-900 leading-tight group-hover:italic transition-all duration-300">
                {property.name}
              </h3>
            </div>
            {askingPrice && (
              <span className="text-lg text-stone-900 font-serif italic shrink-0 ml-4">
                <FormatPrice amount={askingPrice} currency={property.currency || 'EUR'} size="lg" />
              </span>
            )}
          </div>
          
          {property.description && (
            <p className="text-sm text-stone-600 line-clamp-2 mb-3">
              {property.description}
            </p>
          )}
          
          {/* Key Features */}
          <div className="flex items-center gap-4 text-sm text-stone-700 font-sans">
            {property.propertyType && (
              <>
                <span className="font-medium">{property.propertyType.name}</span>
                <span className="w-px h-3 bg-stone-300" />
              </>
            )}
            {property.bedrooms && (
              <span className="flex items-center gap-1">
                <Bed size={12} /> {property.bedrooms}
              </span>
            )}
            {property.bathrooms && (
              <span className="flex items-center gap-1">
                <Bath size={12} /> {property.bathrooms}
              </span>
            )}
            {property.totalFloorArea && (
              <span className="flex items-center gap-1">
                <Maximize size={12} /> {property.totalFloorArea} {property.totalFloorAreaUnit?.toLowerCase() || 'm²'}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// =============================================================================
// PROJECT CARD COMPONENTS (for mixed property + project views)
// =============================================================================

// Human-readable labels for project type enums
function formatProjectType(type: string | null, translations?: Record<string, string>): string {
  if (!type) return translations?.development || 'Development';
  const defaultLabels: Record<string, string> = {
    'APARTMENTS': 'Apartments',
    'TOWNHOUSES': 'Townhouses',
    'LAND_SUBDIVISION': 'Land',
    'HOUSE_AND_LAND_PACKAGES': 'House & Land',
    'MIXED_USE': 'Mixed Use',
    'COMMERCIAL_OFFICE': 'Commercial Office',
    'COMMERCIAL_RETAIL': 'Retail',
    'COMMERCIAL_INDUSTRIAL': 'Industrial',
    'COMMERCIAL_STRATA': 'Strata',
    'OTHER': 'Development',
  };
  const translationKeyMap: Record<string, string> = {
    'APARTMENTS': 'apartments',
    'TOWNHOUSES': 'townhouses',
    'LAND_SUBDIVISION': 'land',
    'HOUSE_AND_LAND_PACKAGES': 'houseAndLand',
    'MIXED_USE': 'mixedUse',
    'COMMERCIAL_OFFICE': 'commercialOffice',
    'COMMERCIAL_RETAIL': 'retail',
    'COMMERCIAL_INDUSTRIAL': 'industrial',
    'COMMERCIAL_STRATA': 'strata',
    'OTHER': 'development',
  };
  const tKey = translationKeyMap[type];
  if (tKey && translations?.[tKey]) return translations[tKey];
  return defaultLabels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatProjectStatus(status: string | null, translations?: Record<string, string>): string | null {
  if (!status) return null;
  const defaultLabels: Record<string, string> = {
    'ACTIVE': 'Now Selling',
    'UPCOMING': 'Coming Soon',
    'SOLD_OUT': 'Sold Out',
    'ON_HOLD': 'On Hold',
    'COMPLETED_ARCHIVED': 'Completed',
  };
  const translationKeyMap: Record<string, string> = {
    'ACTIVE': 'nowSelling',
    'UPCOMING': 'comingSoon',
    'SOLD_OUT': 'soldOut',
    'ON_HOLD': 'onHold',
    'COMPLETED_ARCHIVED': 'completed',
  };
  const tKey = translationKeyMap[status];
  if (tKey && translations?.[tKey]) return translations[tKey];
  return defaultLabels[status] || null;
}

// Project Card Component (Grid View) - 16:9 with HorizonOverlay flag colors
function ProjectCard({ 
  project, 
  basePath, 
  locale,
  getItemRegion: _getItemRegion,
  devTypeMessages,
  devStatusMessages,
  exploreLabel = 'Explore',
}: { 
  project: import('@/components/listings/unified-types').UnifiedProjectItem;
  basePath: string;
  locale: string;
  getItemRegion: (item: UnifiedListingItem) => Region;
  devTypeMessages?: Record<string, string>;
  devStatusMessages?: Record<string, string>;
  exploreLabel?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  const statusLabel = formatProjectStatus(project.status, devStatusMessages);
  const typeLabel = formatProjectType(project.projectType, devTypeMessages);
  
  // Detect region for flag colors using structured location data first
  const projectCountry = project.location?.country || '';
  const projectCountryCode = (project.location?.countryCode || '').toLowerCase();
  const isFrance = projectCountryCode === 'fr'
    || projectCountry.toLowerCase() === 'france'
    || (!projectCountry && !projectCountryCode && detectRegion(project.name) === 'provence');

  const countryLabel = projectCountry
    || (projectCountryCode === 'fr' ? 'France' : projectCountryCode === 'mu' ? 'Mauritius' : '')
    || (isFrance ? 'France' : '');

  const displayLocation = [
    project.location?.suburb,
    project.location?.city,
    project.location?.district,
    project.location?.neighbourhood,
    project.location?.region || project.location?.country,
  ].filter(Boolean).join(', ') || project.location?.name || '';

  return (
    <Link 
      href={`${basePath}/projects/${project.slug || project.id}`}
      className="block"
    >
      <div
        className="group relative w-full cursor-pointer flex flex-col"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image Container */}
        <div className="relative aspect-4/3 overflow-hidden bg-stone-200">
          {project.heroImageUrl ? (
            <Image
              src={project.heroImageUrl}
              alt={project.name}
              fill
              className={`object-cover transition-transform duration-2000 ease-out will-change-transform ${
                isHovered ? 'scale-105' : 'scale-100'
              }`}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          ) : (
            <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200 flex items-center justify-center">
              <Building2 size={48} className="text-stone-400" strokeWidth={1} />
            </div>
          )}
          
          <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />
          
          {/* Center Floating "Explore" Button */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
            <div 
              className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                isHovered ? 'w-28 h-28 opacity-100' : 'w-14 h-14 opacity-0 translate-y-4'
              }`}
            >
              <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
              <span className="text-white font-serif italic text-lg tracking-widest drop-shadow-md">
                {exploreLabel}
              </span>
            </div>
          </div>
          
          {/* Project Type Badge - Top Left */}
          <div className="absolute top-6 left-6 px-3 py-1.5 bg-white/95 text-[10px] tracking-[0.2em] uppercase font-sans text-stone-900 flex items-center gap-2 backdrop-blur-sm z-20">
            <Building2 size={12} strokeWidth={1.5} />
            {typeLabel}
          </div>
          
          {/* Status Badge - Top Right */}
          {statusLabel && (
            <div className="absolute top-6 right-6 px-3 py-1.5 bg-stone-900 text-[10px] tracking-[0.2em] uppercase font-sans text-white z-20">
              {statusLabel}
            </div>
          )}
          
          {/* Location Label - Bottom Left */}
          <div className="absolute bottom-6 left-6 z-20 overflow-hidden">
            <p 
              className={`text-[10px] uppercase tracking-[0.3em] text-stone-200 font-sans font-medium transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                isHovered ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: '100ms' }}
            >
              {displayLocation || 'Development Project'}
            </p>
          </div>
        </div>
        
        {/* Content Section */}
        <div className="flex flex-col gap-4 pt-6">
          {/* Title */}
          <h3 className={`font-serif text-2xl md:text-3xl text-stone-900 leading-tight transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}>
            {project.name}
          </h3>

          {/* Price (on its own line for long price text) */}
          {project.displayPriceText && (
            <p className="font-sans text-sm text-stone-700 line-clamp-2 leading-relaxed">
              {project.displayPriceText}
            </p>
          )}

          <div className="border-t border-stone-200" />
          
          {/* Metadata Row */}
          <div className="flex flex-wrap items-center gap-3 text-stone-600 font-sans text-sm tracking-wider">
            {project.projectType && (
              <>
                <span className="text-stone-900 font-medium">{typeLabel}</span>
                <span className="w-px h-3 bg-stone-300" />
              </>
            )}
            {/* Total Units - Hidden per client request */}
            {project.estimatedCompletionDate && (
              <>
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-stone-400" strokeWidth={1.5} /> {new Date(project.estimatedCompletionDate).toLocaleDateString(locale, { month: 'short', year: 'numeric' })}
                </span>
              </>
            )}
            {countryLabel && (
              <span className={`ml-auto transition-colors duration-500 uppercase text-xs tracking-[0.2em] ${
                isFrance ? 'group-hover:text-blue-900' : 'group-hover:text-teal-800'
              }`}>
                {countryLabel}
              </span>
            )}
          </div>
          
          {/* Progress Bar - Hidden per client request */}
        </div>
      </div>
    </Link>
  );
}

// Project List Card Component (List View)
function ProjectListCard({ 
  project, 
  basePath, 
  locale,
  getItemRegion,
  devTypeMessages,
  devStatusMessages,
}: { 
  project: import('@/components/listings/unified-types').UnifiedProjectItem;
  basePath: string;
  locale: string;
  getItemRegion: (item: UnifiedListingItem) => Region;
  devTypeMessages?: Record<string, string>;
  devStatusMessages?: Record<string, string>;
}) {
  const region = getItemRegion(project);
  
  const statusLabel = formatProjectStatus(project.status, devStatusMessages);
  const typeLabel = formatProjectType(project.projectType, devTypeMessages);

  return (
    <Link 
      href={`${basePath}/projects/${project.slug || project.id}`}
      className="group block py-6 border-b border-stone-200 hover:bg-stone-100/50 transition-colors"
    >
      <div className="flex gap-6">
        {/* Image */}
        <div className="relative w-48 h-32 shrink-0 overflow-hidden bg-stone-200">
          {project.heroImageUrl ? (
            <Image
              src={project.heroImageUrl}
              alt={project.name}
              fill
              className="object-cover transition-transform duration-1500 ease-out group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200 flex items-center justify-center">
              <Building2 size={32} className="text-stone-400" strokeWidth={1} />
            </div>
          )}
          
          {/* Project Type Badge */}
          <div className="absolute top-2 left-2 px-2 py-1 bg-white/95 text-[9px] tracking-[0.15em] uppercase font-sans text-stone-900 flex items-center gap-1 backdrop-blur-sm">
            <Building2 size={10} strokeWidth={1.5} />
            {typeLabel}
          </div>
          
          <div className={`absolute bottom-2 left-2 h-4 w-0.5 ${
            region === 'PROVENCE' ? 'bg-amber-500' : 'bg-red-500'
          }`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-stone-500 uppercase tracking-widest font-sans">
                  {region}
                </span>
                {statusLabel && (
                  <>
                    <span className="w-px h-3 bg-stone-300" />
                    <span className="text-xs text-stone-700 uppercase tracking-widest font-sans font-medium">
                      {statusLabel}
                    </span>
                  </>
                )}
              </div>
              <h3 className="font-serif text-xl text-stone-900 leading-tight group-hover:italic transition-all duration-300">
                {project.name}
              </h3>
            </div>
          </div>
          
          {project.displayPriceText && (
            <p className="text-sm text-stone-600 line-clamp-1 mb-2">
              {project.displayPriceText}
            </p>
          )}
          
          {project.description && (
            <p className="text-sm text-stone-500 line-clamp-2 mb-3">
              {project.description}
            </p>
          )}
          
          {/* Project Details */}
          <div className="flex items-center gap-4 text-sm text-stone-700 font-sans">
            {project.projectType && (
              <>
                <span className="font-medium">{typeLabel}</span>
                <span className="w-px h-3 bg-stone-300" />
              </>
            )}
            {/* Unit counts / availability - Hidden per client request */}
            {project.estimatedCompletionDate && (
              <span className="flex items-center gap-1">
                <Calendar size={12} strokeWidth={1.5} /> Est. {new Date(project.estimatedCompletionDate).toLocaleDateString(locale, { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
